/**
 * Marketing home — `/`. Public-only; if the visitor is already
 * signed in we bounce them to /chat from the route loader.
 */

import { ArrowRight, Brain, Database, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { Link } from "react-router-dom";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { redirectIfAuthed } from "../lib/loaders";

export async function landingLoader({ request }: { request: Request }): Promise<null> {
  return redirectIfAuthed(request);
}

export default function MarketingHome() {
  return (
    <div className="relative flex min-h-screen flex-col bg-cream">
      <SiteHeader />

      <main className="relative isolate flex-1">
        <div aria-hidden className="grain-bg absolute inset-x-0 top-0 h-[640px]" />

        {/* HERO */}
        <section className="container relative flex flex-col items-center pb-24 pt-20 text-center md:pb-32 md:pt-28">
          <Badge
            variant="lemon"
            className="mb-6 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px]"
          >
            <Sparkles className="h-3 w-3" />
            Cloud-native Agent OS
          </Badge>

          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-7xl">
            Production-grade agents your team
            <br className="hidden md:block" /> actually trusts.
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            knode-os pairs an open agent runtime with a first-class knowledge platform and a
            continuous optimization loop. Bring your own LLM keys, tools, and channels — we bring
            the runtime, the wiki, and the trace-driven feedback that makes your agents cheaper,
            faster, and more accurate every week.
          </p>

          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-full px-7 shadow-md">
              <Link to="/login">
                Sign in
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7">
              <a href="https://github.com/knode-os/knode-os" target="_blank" rel="noreferrer">
                View on GitHub
              </a>
            </Button>
          </div>

          {/* Schematic workspace mock — pure CSS, no real data. */}
          <div className="relative mt-20 w-full max-w-5xl">
            <div className="absolute -inset-x-12 -top-10 -bottom-10 -z-10 rounded-[3rem] bg-gradient-to-br from-primary/15 via-transparent to-[hsl(var(--lemon))]/15 blur-3xl" />
            <Card className="overflow-hidden rounded-3xl border-border/60 shadow-2xl">
              <CardContent className="p-0">
                <MockConsole />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FEATURES */}
        <section
          id="features"
          className="relative border-t border-border/60 bg-background/80 py-24"
        >
          <div className="container grid gap-10 md:grid-cols-3">
            <FeatureCard
              icon={<Database className="h-5 w-5" />}
              title="Knowledge base, not 'RAG'"
              body="Per-team S3-backed raw / processed / wiki filesystem with REST + MCP. Versioned wiki pages, deterministic chunk IDs, OCR + embedding pipelines built in."
            />
            <FeatureCard
              icon={<Zap className="h-5 w-5" />}
              title="Bring your own everything"
              body="LLM keys, tools, skills, MCP servers, channels — all behind stable adapters. Built on openai-agents-sdk so tool calling, hand-offs, and guardrails are first-class."
            />
            <FeatureCard
              icon={<Brain className="h-5 w-5" />}
              title="Continuous optimization"
              body="Every run emits OTel traces. The worker samples them nightly to compute regressions per skill, propose prompt rewrites, and route A/B experiments — never auto-deployed without your approval."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-5 w-5" />}
              title="Multi-tenant by default"
              body="Every domain table inherits a tenant context. RBAC, SSO-ready, per-team budgets and rate limits enforced at the gateway. Quiet failures are bugs, not features."
            />
            <FeatureCard
              icon={<Sparkles className="h-5 w-5" />}
              title="Channels that actually ship"
              body="Bind agents to Feishu, Lark, and your own surfaces via a uniform ChannelAdapter. Inbound webhooks, outbound clients, signature verification — same contract for every channel."
            />
            <FeatureCard
              icon={<ArrowRight className="h-5 w-5" />}
              title="Honest UX over magic"
              body="Budgets, approvals, tool risk tiers, and failure modes are visible to operators. Silent retries and hidden auto-learning don't ship."
            />
          </div>
        </section>

        {/* WORKSPACE TEASER */}
        <section id="workspace" className="relative border-t border-border/60 py-24">
          <div className="container max-w-4xl text-center">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
              A workspace built around the work
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">
              Browse agents, edit the knowledge base, follow run traces, configure channels, and
              resolve approvals — all from one console. Personal workspace ships on day one; team
              workspaces and shared knowledge bases follow.
            </p>
            <div className="mt-9">
              <Button asChild size="lg" variant="default" className="rounded-full px-7">
                <Link to="/login">
                  Sign in
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Card className="rounded-2xl border-border/60 bg-card/70 backdrop-blur">
      <CardContent className="p-7">
        <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {icon}
        </div>
        <h3 className="mb-2 font-serif text-xl font-semibold tracking-tight">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function MockConsole() {
  const navItems = [
    { label: "Agents", active: true },
    { label: "Knowledge", active: false },
    { label: "Channels", active: false },
    { label: "LLM", active: false },
    { label: "MCP", active: false },
    { label: "Evaluations", active: false },
    { label: "Teams", active: false },
  ];

  return (
    <div className="grid h-[420px] grid-cols-[200px_1fr] bg-background">
      <aside className="flex flex-col gap-1 border-r border-border bg-secondary/40 p-4">
        <div className="mb-3 inline-flex items-center gap-2 px-2 text-xs font-semibold tracking-tight">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[hsl(var(--clay))]/20 text-[10px] text-clay">
            ◆
          </span>
          knode-os
        </div>
        {navItems.map((item) => (
          <div
            key={item.label}
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2 text-xs",
              item.active ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground",
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
            {item.label}
          </div>
        ))}
      </aside>
      <div className="flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="font-serif text-lg font-semibold leading-tight">Agents</div>
            <div className="text-[11px] text-muted-foreground">
              research-assistant · streaming · gpt-4o
            </div>
          </div>
          <div className="rounded-full border border-border bg-card px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            team · platform-eng
          </div>
        </div>
        <div className="flex-1 space-y-4 overflow-hidden p-6 text-sm">
          <div className="ml-auto max-w-[70%] rounded-2xl bg-[hsl(var(--lemon))]/30 px-4 py-2.5 text-xs text-foreground">
            Summarize this week's design review notes from the wiki.
          </div>
          <div className="max-w-[80%] rounded-2xl border border-border bg-card px-4 py-3 text-xs text-foreground">
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              research-assistant
            </div>
            <p>
              Three threads dominated this week: the sidebar refactor (approved), the KB ingestion
              backlog (one critical regression), and the eval rubric for support-triage. Action
              items are in <span className="text-primary">wiki/design-review/2026-04-26.md</span>.
            </p>
            <div className="mt-2 text-[10px] text-muted-foreground">
              1,283 tok · $0.0029 · 4.2s · 2 KB citations
            </div>
          </div>
        </div>
        <div className="border-t border-border bg-background px-6 py-4">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground shadow-sm">
            <span>Type a message…</span>
            <span className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
              ↵
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
